import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MoveDown } from "lucide-react";
import { Link } from "react-router-dom";
import { MaskLine } from "@/components/Reveal";
import { sharedImage } from "@/lib/images";
import site from "@/data/content/site.json";

const hero = site.hero;
const HERO_IMG = sharedImage(hero.image);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Subtle parallax on the hero image.
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  const scrollToStays = () => {
    const el = document.getElementById("stays");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[46vh] min-h-[360px] md:h-[92vh] w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt={hero.imageAlt}
          className="h-full w-full object-cover object-[center_72%]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-brown/50 via-brown/25 to-brown/60" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
        <div className="max-w-2xl mx-auto md:mx-0 text-center md:text-left">
          <h1 className="font-serif text-ivory text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-7xl font-semibold">
            <MaskLine delay={0.1}>{hero.headlineLines[0]}</MaskLine>
            <MaskLine delay={0.25}>{hero.headlineLines[1]}</MaskLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-5 flex flex-nowrap items-center justify-center md:justify-start gap-3"
          >
            <button
              onClick={scrollToStays}
              data-testid="hero-explore-stays"
              className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-medium text-white hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300"
            >
              {hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/contact"
              data-testid="hero-contact-us"
              className="inline-flex items-center rounded-full border border-ivory/70 px-5 py-2.5 text-sm font-medium text-ivory hover:bg-ivory hover:text-brown transition-colors duration-300"
            >
              {hero.ctaSecondary}
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={scrollToStays}
        aria-label="Scroll to stays"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center text-ivory/80"
      >
        <MoveDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
};

export default Hero;
